import { ArrowLeft, MessageSquare, Bell, RefreshCw, Award, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { PAGE_SEO } from '../seo';

// AWS Marketplace listing URL
const MARKETPLACE_URL = import.meta.env.VITE_MARKETPLACE_URL || 'https://aws.amazon.com/marketplace/search/results?searchTerms=PartnerBuddy';

export default function Features() {
  return (
    <div className="min-h-screen">
      <SEOHead page={PAGE_SEO.features} />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-pb-gray-50 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Link
            to="/"
            className="inline-flex items-center text-pb-gray-500 hover:text-pb-orange mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-pb-blue mb-6">
            Your ACE pipeline should drive revenue,{' '}
            <span className="text-pb-orange">not busywork.</span>
          </h1>
          <p className="text-xl text-pb-gray-600 leading-relaxed max-w-3xl mx-auto">
            For AWS partners who need pipeline visibility without portal pain, PartnerBuddy is a
            conversational ACE assistant that connects directly to AWS Partner Central — no connector
            platform required. Your team simply asks questions and gets answers in Slack or Teams.
          </p>
        </div>
      </section>

      {/* Differentiator Section */}
      <section className="py-12 px-4 bg-pb-blue">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xl text-white leading-relaxed">
            Unlike enterprise platforms that require you to purchase a connector, integrate your CRM,
            and train your team, <strong>PartnerBuddy works where your team already works</strong> — powered
            by AI that actually understands ACE.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-pb-blue text-center mb-12">
            What You Get
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Feature 1 */}
            <div className="bg-pb-gray-50 border border-pb-gray-200 rounded-2xl p-8 hover:shadow-rhythmic transition-all">
              <div className="bg-pb-orange/10 rounded-lg p-3 w-fit mb-4">
                <MessageSquare className="w-8 h-8 text-pb-orange" />
              </div>
              <h3 className="text-xl font-semibold text-pb-blue mb-3">
                Never Touch Partner Central
              </h3>
              <p className="text-pb-gray-600 mb-4">
                Query, update, and create ACE opportunities from Slack or Teams — in plain English.
              </p>
              <div className="bg-white rounded-lg p-4 space-y-2">
                <p className="text-pb-gray-700 font-mono text-sm">"@PartnerBuddy Show me stale opportunities."</p>
                <p className="text-pb-gray-700 font-mono text-sm">"@PartnerBuddy What's closing this quarter?"</p>
                <p className="text-pb-gray-700 font-mono text-sm">"@PartnerBuddy Update the close date for 12345678 to March 15th."</p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-pb-gray-50 border border-pb-gray-200 rounded-2xl p-8 hover:shadow-rhythmic transition-all">
              <div className="bg-pb-orange/10 rounded-lg p-3 w-fit mb-4">
                <Bell className="w-8 h-8 text-pb-orange" />
              </div>
              <h3 className="text-xl font-semibold text-pb-blue mb-3">
                Proactive Pipeline Alerts
              </h3>
              <p className="text-pb-gray-600 mb-4">
                Automated reports delivered on your schedule. Surface overdue opportunities,
                pipeline changes, and deals at risk before they become problems — without
                anyone having to ask.
              </p>
              <ul className="space-y-2 text-pb-gray-600">
                <li className="flex items-start">
                  <span className="text-pb-orange mr-2">•</span>
                  Daily, weekly, or monthly reports
                </li>
                <li className="flex items-start">
                  <span className="text-pb-orange mr-2">•</span>
                  Stale opportunity alerts
                </li>
                <li className="flex items-start">
                  <span className="text-pb-orange mr-2">•</span>
                  Pipeline change notifications
                </li>
              </ul>
            </div>

            {/* Feature 3 */}
            <div className="bg-pb-gray-50 border border-pb-gray-200 rounded-2xl p-8 hover:shadow-rhythmic transition-all">
              <div className="bg-pb-orange/10 rounded-lg p-3 w-fit mb-4">
                <RefreshCw className="w-8 h-8 text-pb-orange" />
              </div>
              <h3 className="text-xl font-semibold text-pb-blue mb-3">
                Optional CRM Sync
              </h3>
              <p className="text-pb-gray-600 mb-4">
                Connect HubSpot or Salesforce. Deals sync bidirectionally between your CRM
                and ACE — one source of truth, zero double-entry.
              </p>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2 text-pb-gray-600">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.943 6.213l-1.227-3.04a.8.8 0 00-.8-.533H13.08a.8.8 0 00-.8.533L11.04 6.213a.8.8 0 00.8 1.067h2.4v8.8h-2.4a.8.8 0 00-.8 1.067l1.24 3.04a.8.8 0 00.8.533h7.84a.8.8 0 00.8-.533l1.227-3.04a.8.8 0 00-.8-1.067h-2.4V7.28h2.4a.8.8 0 00.796-1.067zM9.76 6.213l-1.227-3.04a.8.8 0 00-.8-.533H.093a.8.8 0 00-.8.533L-1.934 6.213a.8.8 0 00.8 1.067h2.4v8.8h-2.4a.8.8 0 00-.8 1.067l1.227 3.04a.8.8 0 00.8.533h7.84a.8.8 0 00.8-.533l1.227-3.04a.8.8 0 00-.8-1.067h-2.4V7.28h2.4a.8.8 0 00.8-1.067z"/>
                  </svg>
                  <span className="font-medium">HubSpot</span>
                </div>
                <div className="flex items-center gap-2 text-pb-gray-600">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.22 4.668h.003c.347 0 .672.063.978.178l-.647 1.263a1.91 1.91 0 00-.334-.036c-1.07 0-1.944.873-1.944 1.944v.73h2.534v1.457H10.28v6.128H8.56V10.2H6.95V8.747h1.61v-.73c0-2.015 1.645-3.658 3.66-3.658v.309zm4.553 4.08c1.837 0 3.329 1.491 3.329 3.329v.003c0 1.837-1.492 3.326-3.329 3.326-1.84 0-3.332-1.489-3.332-3.326v-.003c0-1.838 1.492-3.33 3.332-3.33zm0 1.457a1.87 1.87 0 00-1.872 1.872v.003a1.87 1.87 0 001.872 1.869 1.867 1.867 0 001.869-1.87v-.002a1.867 1.867 0 00-1.87-1.872z"/>
                  </svg>
                  <span className="font-medium">Salesforce</span>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-pb-gray-50 border border-pb-gray-200 rounded-2xl p-8 hover:shadow-rhythmic transition-all">
              <div className="bg-pb-orange/10 rounded-lg p-3 w-fit mb-4">
                <Award className="w-8 h-8 text-pb-orange" />
              </div>
              <h3 className="text-xl font-semibold text-pb-blue mb-3">
                Built for AWS Partners
              </h3>
              <p className="text-pb-gray-600 mb-4">
                Understands ACE stages, terminology, and business logic out of the box.
                Built by an AWS Advanced Tier Partner who got tired of the same busywork
                you're dealing with.
              </p>
              <ul className="space-y-2 text-pb-gray-600">
                <li className="flex items-start">
                  <span className="text-pb-orange mr-2">•</span>
                  Native ACE stage understanding
                </li>
                <li className="flex items-start">
                  <span className="text-pb-orange mr-2">•</span>
                  Partner Central terminology built-in
                </li>
                <li className="flex items-start">
                  <span className="text-pb-orange mr-2">•</span>
                  No training required
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing CTA Section */}
      <section className="py-20 px-4 bg-pb-blue">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-display font-bold text-white mb-4">
            Get Started for $100/month
          </h2>
          <p className="text-xl text-pb-gray-300 mb-8">
            That's a whole lot of power for a nominal price — and no per-user fees.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={MARKETPLACE_URL}
              className="inline-flex items-center px-6 py-3 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all"
            >
              Subscribe on AWS Marketplace
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
            <a
              href="mailto:sales@rhythmictech.com"
              className="inline-flex items-center px-6 py-3 border border-white/30 text-white rounded-lg font-semibold hover:bg-pb-blue-light transition-all"
            >
              Contact Sales
            </a>
          </div>
          <p className="text-sm text-white/60 mt-4">
            *See AWS Marketplace for full pricing details
          </p>
          <p className="text-sm text-pb-gray-400 mt-6">
            Need to share this internally?{' '}
            <a
              href="/partnerbuddy-one-pager.pdf"
              download
              className="text-pb-orange hover:underline"
            >
              Download the one-pager (PDF)
            </a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
